// src/pages/MenuSemainePage.js
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Seo from '../components/Seo';

const MenuSemainePage = () => {
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const [weekPlan, setWeekPlan] = useState(null);
  const [step, setStep] = useState(1);
  const [preferences, setPreferences] = useState({
    budget: 'moyen', // économique, moyen, premium
    familySize: 2,
    mealsPerDay: 2, // Nombre de repas à planifier par jour
    mealTypes: ['déjeuner', 'dîner'], // Types de repas à planifier
    dailyConstraints: {},
    dietaryRestrictions: [],
    cuisinePreferences: [],
    favoriteDishes: '',
    dislikedIngredients: []
  });

  const days = ['Lundi', 'Mardi', 'Mercredi', 'Jeudi', 'Vendredi', 'Samedi', 'Dimanche'];
  const budgetOptions = ['économique', 'moyen', 'premium'];
  const mealTypes = ['petit-déjeuner', 'déjeuner', 'dîner'];
  const dietaryOptions = ['Végétarien', 'Vegan', 'Sans gluten', 'Sans lactose', 'Paleo', 'Keto'];
  const cuisineOptions = ['Française', 'Italienne', 'Asiatique', 'Mexicaine', 'Indienne', 'Méditerranéenne', 'Américaine'];
  const timeOptions = ['Rapide (< 30min)', 'Moyen (30-60min)', 'Long (> 60min)'];

  // Initialisation des contraintes journalières
  useEffect(() => {
    const initialConstraints = {};
    days.forEach(day => {
      initialConstraints[day] = {
        availableTime: 'Moyen (30-60min)',
        specificPreference: '',
        skipMeal: []
      };
    });
    setPreferences(prev => ({ ...prev, dailyConstraints: initialConstraints }));
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setPreferences({ ...preferences, [name]: value });
  };

  const handleDietaryChange = (option) => {
    const updated = preferences.dietaryRestrictions.includes(option)
      ? preferences.dietaryRestrictions.filter(item => item !== option)
      : [...preferences.dietaryRestrictions, option];
    
    setPreferences({ ...preferences, dietaryRestrictions: updated });
  };

  const handleCuisineChange = (option) => {
    const updated = preferences.cuisinePreferences.includes(option)
      ? preferences.cuisinePreferences.filter(item => item !== option)
      : [...preferences.cuisinePreferences, option];
    
    setPreferences({ ...preferences, cuisinePreferences: updated });
  };

  const handleDailyConstraintChange = (day, field, value) => {
    setPreferences({
      ...preferences,
      dailyConstraints: {
        ...preferences.dailyConstraints,
        [day]: {
          ...preferences.dailyConstraints[day],
          [field]: value
        }
      }
    });
  };

  const handleMealTypeChange = (mealType) => {
    const updatedMealTypes = preferences.mealTypes.includes(mealType)
      ? preferences.mealTypes.filter(type => type !== mealType)
      : [...preferences.mealTypes, mealType];
    
    setPreferences({ 
      ...preferences, 
      mealTypes: updatedMealTypes,
      mealsPerDay: updatedMealTypes.length
    });
  };

  const handleSkipMealChange = (day, mealType) => {
    const currentSkips = preferences.dailyConstraints[day].skipMeal || [];
    const updatedSkips = currentSkips.includes(mealType)
      ? currentSkips.filter(type => type !== mealType)
      : [...currentSkips, mealType];
    
    handleDailyConstraintChange(day, 'skipMeal', updatedSkips);
  };

  const nextStep = () => {
    setStep(step + 1);
    window.scrollTo(0, 0);
  };

  const prevStep = () => {
    setStep(step - 1);
    window.scrollTo(0, 0);
  };

  const generateWeekMenu = async () => {
    setIsLoading(true);
    
    try {
      const response = await fetch('/api/generate-week-menu', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(preferences),
      });

      if (!response.ok) {
        throw new Error('Erreur lors de la génération du menu');
      }

      const data = await response.json();
      setWeekPlan(data.weekPlan);
      setStep(4); // Passer à l'étape d'affichage du menu
    } catch (error) {
      console.error("Erreur:", error);
      alert("Une erreur est survenue lors de la génération du menu.");
    } finally {
      setIsLoading(false);
    }
  };

  const saveWeekMenu = async () => {
    if (!weekPlan) return;
    
    try {
      await fetch('/api/save-week-menu', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          weekPlan,
          preferences,
          generatedAt: new Date(),
        }),
      });
      
      alert('Menu de la semaine sauvegardé avec succès!');
    } catch (error) {
      console.error("Erreur:", error);
      alert("Une erreur est survenue lors de la sauvegarde du menu.");
    }
  };

  const generateShoppingList = async () => {
    if (!weekPlan) return;
    
    try {
      const response = await fetch('/api/generate-shopping-list', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ weekPlan }),
      });

      if (!response.ok) {
        throw new Error('Erreur lors de la génération de la liste de courses');
      }

      const data = await response.json();
      // Rediriger vers la page de liste de courses avec les données générées
      navigate(`/liste-courses/${data.shoppingListId}`);
    } catch (error) {
      console.error("Erreur:", error);
      alert("Une erreur est survenue lors de la génération de la liste de courses.");
    }
  };

  const renderStepOne = () => (
    <div className="bg-white rounded-lg shadow-md p-6 mb-8 border border-gray-100">
      <h2 className="text-xl font-semibold mb-4 text-[#2C3E50]">Informations générales</h2>
      
      <div className="mb-4">
        <label className="block text-[#2C3E50] font-medium mb-2">Budget</label>
        <div className="flex gap-4">
          {budgetOptions.map(budget => (
            <label key={budget} className="flex items-center">
              <input
                type="radio"
                name="budget"
                value={budget}
                checked={preferences.budget === budget}
                onChange={handleChange}
                className="mr-2 accent-[#87A96B]"
              />
              <span className="text-[#2C3E50]">{budget.charAt(0).toUpperCase() + budget.slice(1)}</span>
            </label>
          ))}
        </div>
      </div>
      
      <div className="mb-4">
        <label className="block text-[#2C3E50] font-medium mb-2">
          Nombre de personnes: {preferences.familySize}
        </label>
        <input 
          type="range" 
          name="familySize"
          min="1" 
          max="10" 
          value={preferences.familySize}
          onChange={handleChange}
          className="w-full accent-[#87A96B]"
        />
        <div className="flex justify-between text-xs text-gray-500">
          <span>1</span>
          <span>10</span>
        </div>
      </div>
      
      <div className="mb-6">
        <label className="block text-[#2C3E50] font-medium mb-2">Quels repas voulez-vous planifier?</label>
        <div className="flex flex-wrap gap-2">
          {mealTypes.map(type => (
            <button
              type="button"
              key={type}
              onClick={() => handleMealTypeChange(type)}
              className={`px-3 py-1 rounded-full text-sm transition-colors ${
                preferences.mealTypes.includes(type)
                  ? 'bg-[#87A96B] text-white'
                  : 'bg-gray-200 text-[#2C3E50] hover:bg-[#87A96B]/10'
              }`}
            >
              {type.charAt(0).toUpperCase() + type.slice(1)}
            </button>
          ))}
        </div>
      </div>
      
      <div className="mb-6">
        <label className="block text-[#2C3E50] font-medium mb-2">Restrictions alimentaires</label>
        <div className="flex flex-wrap gap-2">
          {dietaryOptions.map(option => (
            <button
              type="button"
              key={option}
              onClick={() => handleDietaryChange(option)}
              className={`px-3 py-1 rounded-full text-sm transition-colors ${
                preferences.dietaryRestrictions.includes(option)
                  ? 'bg-[#87A96B] text-white'
                  : 'bg-gray-200 text-[#2C3E50] hover:bg-[#87A96B]/10'
              }`}
            >
              {option}
            </button>
          ))}
        </div>
      </div>
      
      <div className="mb-6">
        <label className="block text-[#2C3E50] font-medium mb-2">Préférences culinaires</label>
        <div className="flex flex-wrap gap-2">
          {cuisineOptions.map(option => (
            <button
              type="button"
              key={option}
              onClick={() => handleCuisineChange(option)}
              className={`px-3 py-1 rounded-full text-sm transition-colors ${
                preferences.cuisinePreferences.includes(option)
                  ? 'bg-[#87A96B] text-white'
                  : 'bg-gray-200 text-[#2C3E50] hover:bg-[#87A96B]/10'
              }`}
            >
              {option}
            </button>
          ))}
        </div>
      </div>
      
      <div className="mb-6">
        <label className="block text-[#2C3E50] font-medium mb-2">Plats favoris (nous essaierons de les inclure)</label>
        <textarea
          name="favoriteDishes"
          value={preferences.favoriteDishes}
          onChange={handleChange}
          placeholder="Ex: lasagnes, poulet rôti, risotto aux champignons..."
          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-[#87A96B] focus:border-[#87A96B] transition-colors"
          rows="2"
        ></textarea>
      </div>
      
      <div className="mb-6">
        <label className="block text-[#2C3E50] font-medium mb-2">Ingrédients à éviter</label>
        <textarea
          name="dislikedIngredients"
          value={Array.isArray(preferences.dislikedIngredients) 
            ? preferences.dislikedIngredients.join(', ') 
            : preferences.dislikedIngredients}
          onChange={(e) => setPreferences({
            ...preferences, 
            dislikedIngredients: e.target.value.split(',').map(item => item.trim())
          })}
          placeholder="Ex: champignons, olives, foie gras..."
          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-[#87A96B] focus:border-[#87A96B] transition-colors"
          rows="2"
        ></textarea>
        <p className="text-sm text-gray-500">Séparez les ingrédients par des virgules</p>
      </div>
      
      <button
        onClick={nextStep}
        className="w-full bg-[#87A96B] text-white py-3 rounded-md hover:bg-[#87A96B]/90 transition-colors font-medium shadow-md"
      >
        Suivant: Contraintes journalières
      </button>
    </div>
  );

  const renderStepTwo = () => (
    <div className="bg-white rounded-lg shadow-md p-6 mb-8 border border-gray-100">
      <h2 className="text-xl font-semibold mb-6 text-[#2C3E50]">Contraintes journalières</h2>
      <p className="text-gray-600 mb-6">
        Personnalisez votre menu pour chaque jour de la semaine en fonction de vos contraintes de temps et préférences.
      </p>
      
      {days.map((day, index) => (
        <div key={day} className={`border-b border-gray-100 pb-4 ${index === days.length - 1 ? '' : 'mb-4'}`}>
          <h3 className="font-medium text-lg mb-3 text-[#2C3E50]">{day}</h3>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
            <div>
              <label className="block text-[#2C3E50] font-medium mb-2">Temps disponible</label>
              <select
                value={preferences.dailyConstraints[day]?.availableTime || ''}
                onChange={(e) => handleDailyConstraintChange(day, 'availableTime', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-[#87A96B] focus:border-[#87A96B] transition-colors"
              >
                {timeOptions.map(option => (
                  <option key={option} value={option}>{option}</option>
                ))}
              </select>
            </div>
            
            <div>
              <label className="block text-[#2C3E50] font-medium mb-2">Envie particulière (optionnel)</label>
              <input
                type="text"
                value={preferences.dailyConstraints[day]?.specificPreference || ''}
                onChange={(e) => handleDailyConstraintChange(day, 'specificPreference', e.target.value)}
                placeholder="Ex: Italien, léger, protéiné..."
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-[#87A96B] focus:border-[#87A96B] transition-colors"
              />
            </div>
          </div>
          
          <div>
            <label className="block text-[#2C3E50] font-medium mb-2">Repas à ne pas planifier</label>
            <div className="flex flex-wrap gap-2">
              {preferences.mealTypes.map(type => (
                <button
                  type="button"
                  key={`${day}-${type}`}
                  onClick={() => handleSkipMealChange(day, type)}
                  className={`px-3 py-1 rounded-full text-sm transition-colors ${
                    preferences.dailyConstraints[day]?.skipMeal?.includes(type)
                      ? 'bg-red-500 text-white'
                      : 'bg-gray-200 text-[#2C3E50] hover:bg-red-100'
                  }`}
                >
                  {type.charAt(0).toUpperCase() + type.slice(1)}
                </button>
              ))}
            </div>
          </div>
        </div>
      ))}
      
      <div className="flex justify-between mt-6">
        <button
          onClick={prevStep}
          className="px-6 py-2 bg-[#2C3E50] text-white rounded-md hover:bg-[#2C3E50]/90 transition-colors font-medium"
        >
          Retour
        </button>
        
        <button
          onClick={nextStep}
          className="px-6 py-2 bg-[#87A96B] text-white rounded-md hover:bg-[#87A96B]/90 transition-colors font-medium"
        >
          Suivant: Confirmation
        </button>
      </div>
    </div>
  );

  const renderStepThree = () => (
    <div className="bg-white rounded-lg shadow-md p-6 mb-8 border border-gray-100">
      <h2 className="text-xl font-semibold mb-6 text-[#2C3E50]">Confirmation des préférences</h2>
      
      <div className="mb-6">
        <h3 className="font-medium mb-2 text-[#2C3E50]">Informations générales</h3>
        <div className="bg-[#87A96B]/5 p-4 rounded-md border border-[#87A96B]/20">
          <p className="text-[#2C3E50]"><span className="font-medium">Budget:</span> {preferences.budget}</p>
          <p className="text-[#2C3E50]"><span className="font-medium">Nombre de personnes:</span> {preferences.familySize}</p>
          <p className="text-[#2C3E50]"><span className="font-medium">Repas à planifier:</span> {preferences.mealTypes.join(', ')}</p>
          <p className="text-[#2C3E50]"><span className="font-medium">Restrictions alimentaires:</span> {preferences.dietaryRestrictions.length ? preferences.dietaryRestrictions.join(', ') : 'Aucune'}</p>
          <p className="text-[#2C3E50]"><span className="font-medium">Cuisines préférées:</span> {preferences.cuisinePreferences.length ? preferences.cuisinePreferences.join(', ') : 'Toutes'}</p>
          <p className="text-[#2C3E50]"><span className="font-medium">Plats favoris:</span> {preferences.favoriteDishes || 'Aucun spécifié'}</p>
          <p className="text-[#2C3E50]"><span className="font-medium">Ingrédients à éviter:</span> {Array.isArray(preferences.dislikedIngredients) && preferences.dislikedIngredients.length ? preferences.dislikedIngredients.join(', ') : 'Aucun'}</p>
        </div>
      </div>
      
      <div className="mb-6">
        <h3 className="font-medium mb-2 text-[#2C3E50]">Contraintes journalières</h3>
        <div className="bg-[#E67E22]/5 p-4 rounded-md border border-[#E67E22]/20">
          {days.map(day => (
            <div key={day} className="mb-2">
              <p className="font-medium text-[#2C3E50]">{day}:</p>
              <ul className="pl-5 text-sm text-[#2C3E50]">
                <li>Temps disponible: {preferences.dailyConstraints[day]?.availableTime}</li>
                {preferences.dailyConstraints[day]?.specificPreference && (
                  <li>Envie particulière: {preferences.dailyConstraints[day].specificPreference}</li>
                )}
                {preferences.dailyConstraints[day]?.skipMeal?.length > 0 && (
                  <li>Repas non planifiés: {preferences.dailyConstraints[day].skipMeal.join(', ')}</li>
                )}
              </ul>
            </div>
          ))}
        </div>
      </div>
      
      <div className="flex justify-between">
        <button
          onClick={prevStep}
          className="px-6 py-2 bg-[#2C3E50] text-white rounded-md hover:bg-[#2C3E50]/90 transition-colors font-medium"
        >
          Retour
        </button>
        
        <button
          onClick={generateWeekMenu}
          disabled={isLoading}
          className="px-6 py-2 bg-[#87A96B] text-white rounded-md hover:bg-[#87A96B]/90 transition-colors flex items-center font-medium shadow-md"
        >
          {isLoading ? (
            <>
              <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
              Génération en cours...
            </>
          ) : "Générer mon menu de la semaine"}
        </button>
      </div>
    </div>
  );

  const renderStepFour = () => (
    <div className="bg-white rounded-lg shadow-md p-6 mb-8 border border-gray-100">
      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center mb-6 gap-4">
        <h2 className="text-xl font-semibold text-[#2C3E50]">Votre menu de la semaine</h2>
        <div className="flex flex-col sm:flex-row gap-2">
          <button
            onClick={saveWeekMenu}
            className="px-4 py-2 bg-[#87A96B] text-white rounded-md hover:bg-[#87A96B]/90 transition-colors text-sm font-medium"
          >
            Sauvegarder
          </button>
          <button
            onClick={generateShoppingList}
            className="px-4 py-2 bg-[#E67E22] text-white rounded-md hover:bg-[#E67E22]/90 transition-colors text-sm font-medium"
          >
            Générer la liste de courses
          </button>
        </div>
      </div>
      
      <div className="grid grid-cols-1 gap-6">
        {days.map((day) => (
          <div key={day} className="border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow">
            <h3 className="font-bold text-lg mb-3 text-[#2C3E50] border-b border-[#87A96B]/20 pb-2">{day}</h3>
            
            {weekPlan && weekPlan[day] ? (
              <div>
                {Object.entries(weekPlan[day]).map(([mealType, meal]) => (
                  // Ne pas afficher les repas qui ont été sautés
                  !preferences.dailyConstraints[day]?.skipMeal?.includes(mealType) && (
                    <div key={mealType} className="mb-4 last:mb-0">
                      <h4 className="font-medium text-[#87A96B] mb-2">
                        {mealType.charAt(0).toUpperCase() + mealType.slice(1)}
                      </h4>
                      
                      <div className="bg-gray-50 p-3 rounded-md border border-gray-100">
                        <div className="flex justify-between items-center mb-2">
                          <p className="font-semibold text-[#2C3E50]">{meal.title}</p>
                          <span className="text-sm text-gray-500">⏱️ {meal.prepTime} min</span>
                        </div>
                        
                        <p className="text-sm text-gray-600 mb-2">{meal.description}</p>
                        
                        <div className="mt-2 text-sm">
                          <button 
                            className="text-[#E67E22] hover:text-[#E67E22]/80 mr-4 font-medium transition-colors"
                            onClick={() => navigate(`/recette/${meal.id}`)}
                          >
                            Voir la recette
                          </button>
                          
                          <button 
                            className="text-[#2C3E50] hover:text-[#2C3E50]/70 font-medium transition-colors"
                            onClick={() => {
                              // Remplacer ce repas dans le menu
                              navigate({
                                pathname: '/inspiration-repas',
                                search: `?replace=true&day=${day}&mealType=${mealType}&weekPlanId=${weekPlan.id}`
                              });
                            }}
                          >
                            Remplacer
                          </button>
                        </div>
                      </div>
                    </div>
                  )
                ))}
              </div>
            ) : (
              <p className="text-gray-500 italic">Aucun repas planifié pour ce jour</p>
            )}
          </div>
        ))}
      </div>
      
      <div className="flex flex-col sm:flex-row sm:justify-between mt-6 gap-4">
        <button
          onClick={() => setStep(3)}
          className="px-6 py-2 bg-[#2C3E50] text-white rounded-md hover:bg-[#2C3E50]/90 transition-colors font-medium"
        >
          Modifier les préférences
        </button>
        
        <button
          onClick={generateWeekMenu}
          disabled={isLoading}
          className="px-6 py-2 bg-[#87A96B] text-white rounded-md hover:bg-[#87A96B]/90 transition-colors font-medium"
        >
          {isLoading ? "Génération en cours..." : "Régénérer un menu"}
        </button>
      </div>
    </div>
  );

  return (
    <>
      <Seo
        title="Menu de la semaine"
        description="Configurez vos préférences et générez un menu hebdomadaire personnalisé avec YAQAM."
        breadcrumb
      />
      <div className="max-w-4xl mx-auto px-4 py-8">
      <h1 className="text-3xl font-bold mb-6 text-center text-[#2C3E50]">Menu de la Semaine 📅</h1>
      
      {step < 4 && (
        <div className="flex justify-between mb-8">
          {[1, 2, 3].map((s) => (
            <div 
              key={s} 
              className={`flex items-center ${
                s < step ? 'text-[#87A96B]' : s === step ? 'text-[#E67E22]' : 'text-gray-400'
              }`}
            >
              <div className={`w-8 h-8 rounded-full flex items-center justify-center mr-2 transition-colors ${
                s < step ? 'bg-[#87A96B] text-white' : s === step ? 'bg-[#E67E22] text-white' : 'bg-gray-200'
              }`}>
                {s < step ? '✓' : s}
              </div>
              <span className="hidden sm:inline font-medium">
                {s === 1 && 'Préférences générales'}
                {s === 2 && 'Contraintes journalières'}
                {s === 3 && 'Confirmation'}
              </span>
            </div>
          ))}
        </div>
      )}
      
      {step === 1 && renderStepOne()}
      {step === 2 && renderStepTwo()}
      {step === 3 && renderStepThree()}
      {step === 4 && renderStepFour()}
    </div>
    </>
  );
};

export default MenuSemainePage;
